import { HistoryLogsService } from 'src/app/core/services/history-log.service';
import {
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Observable } from 'rxjs';
import { Company } from 'src/app/core/models/company';
import { AuthenticationService, MODAL_DATA } from '@stockaid/services';
import { CompanyService } from 'src/app/core/services/company.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GridState } from 'src/app/core/infrastructure/classes/grid-state';
import { MetaDataField } from 'src/app/core/infrastructure/classes/meta-data-field';
import { MetaData } from 'src/app/core/infrastructure/classes/meta-data';
import { Logic } from 'src/app/core/infrastructure/enums/logic.enum';
import { MetadataService } from 'src/app/core/services/metadata.service';
import { BaseComponent } from '@stockaid/core';
import { switchMap, tap } from 'rxjs/operators';
import { IUserResponse } from 'src/app/core/infrastructure/interfaces/user-response.interface';
import { User } from 'src/app/core/models/user';
import { GridStateColumn } from 'src/app/core/infrastructure/classes/grid-state-column';
import {
  fillingMissingItems,
  mapColumnStateToField,
  reorderColumnsByFields,
  reorderFields,
  reorderFieldsByColumns,
  sortColumnsByField,
  updateColumnsVisible,
  updateFieldsVisible,
} from '@stockaid/utils';
import { State } from '@progress/kendo-data-query';
import { ColumnComponent, GridComponent } from '@progress/kendo-angular-grid';
import { EntityType, HistoryLog } from 'src/app/core/models/history-log';
import { HistoryLogField } from 'src/app/core/infrastructure/enums/meta-data-field.enum';
import { GridStateColumnSort } from 'src/app/core/infrastructure/classes/grid-state-column-sort';
import { GridStatePagination } from 'src/app/core/infrastructure/classes/grid-state-pagination';
import { IColumnReorderEvent } from 'src/app/core/infrastructure/interfaces/column-reorder.interface';
import { UserService } from 'src/app/core/services/user.service';
import { IGridViewChangeEvent } from 'src/app/core/models/grid-view';
import { TitleCasePipe } from '@angular/common';

@Component({
  selector: 'app-history-log-modal',
  templateUrl: './history-log-modal.component.html',
  styleUrls: ['./history-log-modal.component.scss'],
  providers: [TitleCasePipe]
})
export class HistoryLogModalComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  historyLogRef: HistoryLog = new HistoryLog();
  currentUser: User;
  loading: boolean = true;
  currentCompany: Company;
  metaData!: MetaData;
  metaDataFields: MetaDataField[];
  userMetaDataFields: MetaDataField[];
  gridStateHistoryLog: GridState = new GridState();
  userGridStateHistoryLog: GridState;
  itemGroupMetaDataFields: MetaDataField[] = [];
  itemMetaDataFields: MetaDataField[] = [];
  isFullScreen = false;
  additionalReadOnlyCols: string[] = [
      HistoryLogField.timestampActor,
      HistoryLogField.changes,
      HistoryLogField.context,
  ];

  @Input() entityKey: string;
  @Input() entityType: EntityType;
  @Input() entityName?: string;

  @ViewChild(GridComponent) grid: GridComponent;

  gridState: State = {
    skip: 0,
    take: 20,
    filter: {
      logic: Logic.and,
      filters: [],
    },
  };

  constructor(
    @Inject(MODAL_DATA) public data: any,
    public activeModal: NgbActiveModal,
    public historyLogsService: HistoryLogsService,
    private companyService: CompanyService,
    private metaDataService: MetadataService,
    private readonly authenticationService: AuthenticationService,
    private userService: UserService,
  ) {
    super();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngOnInit(): void {
    this.getCurrentUser()
      .pipe(
        this.autoCleanUp(),
        switchMap(() => this.getCurrentCompany()),
        switchMap(() => this.getHistoryLogsList())
      )
      .subscribe(() => (this.loading = false));
  }

   getCurrentUser(): Observable<IUserResponse> {
    return this.authenticationService.getLoggedInUserFromSession().pipe(
      tap((user) => {
        const [currentUser] = user?.model || [];
        this.currentUser = currentUser;
      })
    );
  }

  getCurrentCompany(): Observable<Company> {
    return this.companyService.getCurrentCompany().pipe(
      this.autoCleanUp(),
      tap((company) => {
        this.currentCompany = company;
      })
    );
  }

 getHistoryLogsList(): Observable<MetaData> {
     this.userGridStateHistoryLog =
       this.currentUser?.gridStateHistoryLogList || {
         skip: 0,
         take: 30,
         sort: [],
         filter: {
           logic: Logic.and,
           filters: [],
         },
       } as unknown as GridState;
     return this.metaDataService.getHistoryLogsMetadata().pipe(
       this.autoCleanUp(),
       tap((metaData) => {
         metaData.fields = metaData.fields.filter(field =>
           field?.displayControl?.displayWithCompanyType?.includes(this.currentCompany?.companyType)
         );
         this.metaData = metaData;
 
         if (
           !Object.keys(this.userGridStateHistoryLog || {})?.length ||
           !this.userGridStateHistoryLog?.columns?.length
         ) {
           this.initGridState();
         }else {
           this.userGridStateHistoryLog.columns = fillingMissingItems(this.metaData?.fields, this.userGridStateHistoryLog.columns);
         }

         this.metaDataFields = mapColumnStateToField(
           this.metaData?.fields,
           this.userGridStateHistoryLog?.columns
         );
         this.metaDataFields = reorderFieldsByColumns(
           this.metaDataFields,
           this.userGridStateHistoryLog?.columns
         );
         this.gridStateHistoryLog = this.userGridStateHistoryLog;
         this.userMetaDataFields = this.metaDataFields;
 
         this.loadGridState();
       })
     );
   }
 
   loadGridState() : void {
     this.gridState.filter = this.userGridStateHistoryLog?.filter;
     this.gridState.sort = this.userGridStateHistoryLog?.sort;
     this.gridState.skip =
       this.userGridStateHistoryLog?.pagination?.paginationCurrentPage || 0;
     this.gridState.take =
       this.userGridStateHistoryLog?.pagination?.paginationPageSize || 100;
   }
 
   initGridState(): void{
     this.gridStateHistoryLog.columns = [];
     this.metaData.fields.forEach((metaDataField) => {
       const column = new GridStateColumn();
       column.name = metaDataField.field;
       column.visible = metaDataField.initialUpload;
       delete column.sort;
 
       this.gridStateHistoryLog?.columns?.push(column);
     })
     this.gridStateHistoryLog.columns = sortColumnsByField(this.gridStateHistoryLog?.columns, 'visible', 'desc')
     this.userGridStateHistoryLog = this.gridStateHistoryLog;
   }
   onColumnChange(changedColumns: ColumnComponent[]): void {
     updateColumnsVisible(this.gridStateHistoryLog?.columns, changedColumns);
 
     updateFieldsVisible(this.metaDataFields, changedColumns);
 
     this.gridStateHistoryLog.columns = sortColumnsByField(this.gridStateHistoryLog?.columns, 'visible', 'desc');
     this.metaDataFields = sortColumnsByField(this.metaDataFields, 'initialUpload', 'desc')
     this.userMetaDataFields = this.metaDataFields;
     this.currentUser.gridStateHistoryLogList = this.gridStateHistoryLog;
 
     this.userService.editUser(this.currentUser).subscribe();
   }
 
   onStateChange(stateEvent): void {
     const {state, isClearClick, removalOrdersFilter} = stateEvent;
     this.gridState = state;
     this.gridStateHistoryLog?.columns.map((column) => {
       const currentSort = state.sort?.find((srt) => srt.field === column.name);
       let currentFilter = state.filter?.filters.find(
         (fltr) => fltr['filters'][0]?.field === column.name
       );
 
       if (currentSort) {
         column.sort = new GridStateColumnSort({
           direction: currentSort.dir as string,
         });
       } else {
         delete column.sort;
       }
 
       if (currentFilter) {
         column.filters = currentFilter;
       } else {
         delete column.filters;
       }
 
       return column;
     });
 
     this.gridStateHistoryLog.pagination = new GridStatePagination({
       paginationCurrentPage: state.skip,
       paginationPageSize: state.take,
     });
 
     this.gridStateHistoryLog.removalOrdersFilter = removalOrdersFilter;
 
     this.currentUser.gridStateHistoryLogList = this.gridStateHistoryLog;
     if(isClearClick) {
       this.currentUser.gridStateHistoryLogList.selectedGridView = null
     }
     this.userService.editUser(this.currentUser).subscribe();
   }
 
   onColumnReorder(column: IColumnReorderEvent): void {
     this.userMetaDataFields = reorderFields(this.userMetaDataFields, column);
 
     this.gridStateHistoryLog.columns = reorderColumnsByFields(
         this.userMetaDataFields,
         this.gridStateHistoryLog?.columns
     );
 
     if (column.isResettingLockedColumns) {
       this.metaDataFields = this.userMetaDataFields;
       this.gridStateHistoryLog.columns.forEach((c) => (c.locked = false));
       this.metaDataFields.forEach((f) => (f.locked = false));
     }
 
     const lockingColumn = this.gridStateHistoryLog.columns
     .find((i) => i.name === column?.column?.field);
     const lockingItemColumn = this.metaDataFields.find((i) => i.field === column?.column?.field);
     if (
       column.isLocked !== undefined &&
       column.isLocked !== null &&
       lockingColumn
     ) {
       lockingColumn.locked = column.isLocked;
       lockingItemColumn.locked = column.isLocked;
     }
     this.currentUser.gridStateHistoryLogList = this.gridStateHistoryLog;
     this.userService.editUser(this.currentUser).subscribe();
   }
 
   onGridViewChange({ selectedGridView, columns, gridState, removalOrdersFilter }: IGridViewChangeEvent): void {
     this.userMetaDataFields = columns;
     this.metaDataFields = columns;
     this.gridStateHistoryLog.columns = reorderColumnsByFields(
       this.metaDataFields,
       this.gridStateHistoryLog?.columns
     );
     this.gridStateHistoryLog.removalOrdersFilter = removalOrdersFilter;
     this.gridStateHistoryLog.selectedGridView = selectedGridView;
     this.gridStateHistoryLog.selectedGridViewState = selectedGridView ? gridState : null ;
     this.onStateChange({ state:gridState });
   }
 
   onColumnResize(column) {
     const { field, newWidth } = column;
     this.gridStateHistoryLog.columns.forEach((column) => {
       if (column.name === field) {
         column.width = newWidth;
       }
     });
     this.currentUser.gridStateHistoryLogList = this.gridStateHistoryLog;
     this.userService.editUser(this.currentUser).subscribe();
   }
}
